import { KID_GAMES } from './topics.english.ts';

/**
 * The For grown-ups page's week (kid engagement PRD F2): seven local days
 * ending today. The server sends eight UTC days so every offset is covered;
 * these functions keep only what falls on the viewer's own seven days.
 */
export const WEEK_DAYS = 7;
const MAX_SUGGESTIONS = 3;

type Talk = { startedAt: string; topic?: string | null };
type Read = { finishedAt: string; book: { title: string; ask?: string | null } };

/** Local calendar days between `iso` and `now`; DST-safe through rounding. */
export function daysAgo(iso: string, now: Date): number {
  const d = new Date(iso);
  const then = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((today - then) / 86_400_000);
}

const inWeek = (iso: string, now: Date) => {
  const n = daysAgo(iso, now);
  return n >= 0 && n < WEEK_DAYS;
};

/** The rows that fall on the seven local days, for sections shown as they come. */
export function thisWeek<T>(rows: readonly T[], when: (row: T) => string, now: Date): T[] {
  return rows.filter((r) => inWeek(when(r), now));
}

/** One flag per day, oldest first and today last. Not a streak: no count. */
export function weekDays(talks: readonly Pick<Talk, 'startedAt'>[], now: Date): boolean[] {
  const days: boolean[] = Array(WEEK_DAYS).fill(false);
  for (const t of talks) if (inWeek(t.startedAt, now)) days[WEEK_DAYS - 1 - daysAgo(t.startedAt, now)] = true;
  return days;
}

export type TopicCount = { topic: string | null; count: number };

/** The week's topics and games, most played first, ties by most recent. */
export function topicCounts(talks: readonly Talk[], now: Date): TopicCount[] {
  const seen = new Map<string | null, { count: number; last: number }>();
  for (const t of talks) {
    if (!inWeek(t.startedAt, now)) continue;
    const key = t.topic ?? null;
    const at = Date.parse(t.startedAt);
    const prev = seen.get(key);
    seen.set(key, { count: (prev?.count ?? 0) + 1, last: Math.max(prev?.last ?? at, at) });
  }
  return [...seen.entries()]
    .sort(([, a], [, b]) => b.count - a.count || b.last - a.last)
    .map(([topic, { count }]) => ({ topic, count }));
}

export type Suggestion =
  | { kind: 'book'; title: string; ask: string }
  | { kind: 'game'; topic: string }
  | { kind: 'topic'; topic: string }
  | { kind: 'none' };

const isGame = (topic: string) => (KID_GAMES as readonly string[]).includes(topic);

/**
 * "Try it together", by fixed rules in order: the newest finished book that
 * carries a question, then each topic or game played by `topicCounts`' order.
 * A week with no talk, or with nothing else to suggest, offers the riddle
 * game. At most three.
 */
export function suggestions(week: { talks: readonly Talk[]; reads: readonly Read[] }, now: Date): Suggestion[] {
  const out: Suggestion[] = [];
  const talked = week.talks.some((t) => inWeek(t.startedAt, now));
  if (!talked) out.push({ kind: 'none' });
  const read = [...week.reads]
    .filter((r) => inWeek(r.finishedAt, now) && r.book.ask)
    .sort((a, b) => Date.parse(b.finishedAt) - Date.parse(a.finishedAt))[0];
  if (read?.book.ask) out.push({ kind: 'book', title: read.book.title, ask: read.book.ask });
  for (const { topic } of topicCounts(week.talks, now)) {
    if (topic === null) continue;
    out.push(isGame(topic) ? { kind: 'game', topic } : { kind: 'topic', topic });
  }
  // A week of free talk alone suggests no topic; the riddle game stands in.
  if (out.length === 0) out.push({ kind: 'none' });
  return out.slice(0, MAX_SUGGESTIONS);
}
